// Memoria del agente (`agent_memories`) + contexto de marca (`brand_profiles`).
//
// El agente guarda datos durables del usuario/marca con `save_memory` y se le
// re-inyectan en el system prompt de cada chat, junto con el contexto de la
// marca activa. Todo scopeado por empresa (RLS por `id_company`).

import { supabase } from '../../lib/supabase';
import { rubroVisible } from '../../lib/onboardingPendiente';

export interface AgentMemory {
  id: string;
  key: string | null;
  content: string;
}

/** Trae las memorias de la empresa, más recientes primero (cap 40). */
export async function fetchMemories(companyId: string): Promise<AgentMemory[]> {
  const { data, error } = await supabase
    .from('agent_memories')
    .select('id, key, content')
    .eq('id_company', companyId)
    .order('updated_at', { ascending: false })
    .limit(40);
  if (error || !data) {
    if (error) console.error('[agentMemoryApi] fetchMemories', error);
    return [];
  }
  return data as AgentMemory[];
}

/** Guarda una memoria. Si viene `key` y ya existe una con ese key en la
 *  empresa, la sobrescribe (en vez de duplicar). */
export async function saveMemory(
  companyId: string,
  content: string,
  key?: string | null,
): Promise<void> {
  const trimmed = content.trim();
  if (!trimmed) return;
  const k = key?.trim() || null;

  if (k) {
    const { data: existing } = await supabase
      .from('agent_memories')
      .select('id')
      .eq('id_company', companyId)
      .eq('key', k)
      .maybeSingle();
    if ((existing as any)?.id) {
      const { error } = await supabase
        .from('agent_memories')
        .update({ content: trimmed, updated_at: new Date().toISOString() })
        .eq('id', (existing as any).id);
      if (error) console.error('[agentMemoryApi] saveMemory update', error);
      return;
    }
  }
  const { error } = await supabase
    .from('agent_memories')
    .insert({ id_company: companyId, content: trimmed, key: k });
  if (error) console.error('[agentMemoryApi] saveMemory insert', error);
}

/** Borra una memoria por id. */
export async function deleteMemory(id: string): Promise<void> {
  const { error } = await supabase.from('agent_memories').delete().eq('id', id);
  if (error) console.error('[agentMemoryApi] deleteMemory', error);
}

export interface BrandContext {
  name: string | null;
  rubro: string | null;
  userDescription: string | null;
  targetPublic: string | null;
  colors: string[];
  /** URL del logo de la marca (si tiene). Usable como frame/referencia. */
  logoUrl: string | null;
}

/** Contexto de la marca ACTIVA del usuario (su `current_brand_id`), con
 *  fallback al brand default de la empresa. Devuelve null si no hay. */
export async function fetchBrandContext(
  userId: string | null,
  companyId: string,
): Promise<BrandContext | null> {
  let brandId: string | null = null;
  if (userId) {
    const { data: prof } = await supabase
      .from('profiles')
      .select('current_brand_id')
      .eq('id', userId)
      .maybeSingle();
    brandId = ((prof as any)?.current_brand_id as string | null) ?? null;
  }

  const cols = 'name, rubro, user_description, target_public, colors, logo_url';
  const { data } = brandId
    ? await supabase
        .from('brand_profiles')
        .select(cols)
        .eq('id', brandId)
        .maybeSingle()
    : await supabase
        .from('brand_profiles')
        .select(cols)
        .eq('id_company', companyId)
        .eq('is_default', true)
        .maybeSingle();

  if (!data) return null;
  const d = data as any;
  return {
    name: d.name ?? null,
    // El centinela no viaja al prompt: `agentSystemPrompt` hace `if (brand.rubro)` y le
    // estaba contando al agente "Rubro: sin-definir", que el agente puede repetirle al
    // cliente. Sin rubro, el bloque de marca simplemente no lo nombra.
    rubro: rubroVisible(d.rubro),
    userDescription: d.user_description ?? null,
    targetPublic: d.target_public ?? null,
    colors: Array.isArray(d.colors)
      ? (d.colors as string[]).filter((c) => typeof c === 'string')
      : [],
    logoUrl: (d.logo_url as string) ?? null,
  };
}
